import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { environment } from "../environments/environment";
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Subject } from 'rxjs';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from "./core/services/auth.service";
import { AuthStatus } from "./core/auth-status.enum";
import { VersionService } from "./core/services/version.service";
import { FeatureService } from "./core/services/feature.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  appTitle = "PatientGo";
  environmentName = "";
  isIframe: boolean;
  updateRequired: boolean = false;

  protected currentVersion = environment.version;
  protected latestVersion = '0.0.0';

  private readonly _destroying$ = new Subject<void>();

  constructor(private titleService: Title, private authService: AuthService, private msalBroadcastService: MsalBroadcastService, private msalService: MsalService, private renderer: Renderer2, private _versionService: VersionService, private _featureService: FeatureService) {
    this.environmentName = environment.environment;

    this._versionService.getVersion().subscribe((rsp: any) => {
      this.latestVersion = rsp.version ? rsp.version : '0.0.0.0';
      this.updateRequired = !this.isCurrentVersion(this.currentVersion, this.latestVersion);
    });
  }

  ngOnInit() {
    this._featureService.load();
    this.isIframe = window !== window.parent && !window.opener;

    // Initialize showTestData in session storage if it doesn't exist
    if (sessionStorage.getItem('showTestData') === null) {
      sessionStorage.setItem('showTestData', 'false');
    }

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      })
    this.titleService.setTitle(this.appTitle);

    // Add class names to the body based on the users authentication status
    this.authService.authStatus.subscribe((status) => {
      if (status === AuthStatus.Authenticated) {
        this.renderer.addClass(document.body, "authenticated");
        this.renderer.removeClass(document.body, "unauthenticated");
      } else {
        this.renderer.removeClass(document.body, "authenticated");
        this.renderer.addClass(document.body, "unauthenticated");
      }
    });

    this.authService.rolesLoaded.subscribe((rolesLoaded) => {
      if (rolesLoaded) {
        this.renderer.addClass(document.body, "roles-loaded");
      } else {
        this.renderer.removeClass(document.body, "roles-loaded");
      }
    });
  }


  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  /**
   * Compares two version numbers and returns true if they are the same
   * Handles different version formats (e.g., "1.28" and "1.28.0" are considered equal)
   * @param source The source version number
   * @param target The target version number to compare against
   * @returns boolean indicating if versions are the same
   */
  isCurrentVersion(source: string, target: string): boolean {
    if (!source || !target) return false;

    // Remove any characters that aren't numbers or periods
    const cleanSource = source.replace(/[^\d.]/g, '');
    const cleanTarget = target.replace(/[^\d.]/g, '');

    // Split version numbers into arrays and pad with zeros if needed
    const sourceParts = cleanSource.split('.').map(Number);
    const targetParts = cleanTarget.split('.').map(Number);

    // Pad the shorter array with zeros
    const maxLength = Math.max(sourceParts.length, targetParts.length);
    while (sourceParts.length < maxLength) sourceParts.push(0);
    while (targetParts.length < maxLength) targetParts.push(0);

    // Compare each part
    for (let i = 0; i < maxLength; i++) {
      if (sourceParts[i] !== targetParts[i]) {
        return false;
      }
    }

    return true;
  }
}
